/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { FocusZone, FocusZoneDirection } from '@fluentui/react-focus';
import { ActionButton, DefaultButton, IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { List } from '@fluentui/react/lib/List';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IScrollablePaneStyleProps, IScrollablePaneStyles, ScrollablePane, ScrollbarVisibility } from '@fluentui/react/lib/ScrollablePane';
import { ISeparatorStyleProps, ISeparatorStyles, Separator } from '@fluentui/react/lib/Separator';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DragAndDropInput } from 'src/core/components/DragAndDropInput';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { AzureContainerBrowsingModal } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/AzureContainerBrowsingModal';
import { TranslatorDemosConfigs } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/TranslatorDemosConfigs';
import { TargetFile, TranslatorLanguage, useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { getBlobStoreUrl, ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';

export type DocumentTranslationDemoPageInputStepProps = {
    sourceLanguageKey: string;
    targetLanguageKeys: string[];
    selectedFiles?: TargetFile[];
    storageAccountsError?: Error;
    selectedStorageAccount?: string;
    downloadTranslatedFiles: boolean;
    isStorageAccountsLoading?: boolean;
    refreshStorageAccounts?: () => void;
    selectedDestinationContainer?: string;
    supportedLanguages: TranslatorLanguage[];
    storageAccounts: AzureStorageAccountStore[];
    onSourceLanguageChange: (newKey: string) => void;
    onTargetLanguageChange: (newKeys: string[]) => void;
    onGlossariesChange: (newGlossaries: TargetFile[]) => void;
    onStorageAccountChange?: (storageAccount: string) => void;
    onFilesChange?: (files: TargetFile[], shouldTranslateContainer: boolean) => void;
    onDestinationChange?: (destinationContainerName: string, downloadTranslatedFiles: boolean) => void;
};

const messages = defineMessages({
    or: { id: 'languageStudio.documentTranslationDemoPage.or', defaultMessage: 'or' },
    browse: { id: 'languageStudio.documentTranslationDemoPage.browse', defaultMessage: 'Browse' },
    nameHeaderText: { id: 'languageStudio.documentTranslationDemoPage.nameHeaderText', defaultMessage: 'Name' },
    goToSettings: { id: 'languageStudio.documentTranslationDemoPage.goToSettings', defaultMessage: 'Go to Settings' },
    browseAzure: { id: 'languageStudio.documentTranslationDemoPage.browseAzure', defaultMessage: 'Azure blob storage' },
    addGlossary: { id: 'languageStudio.documentTranslationDemoPage.addGlossary', defaultMessage: 'Add a glossary file' },
    localFilesSource: { id: 'languageStudio.documentTranslationDemoPage.localFilesSource', defaultMessage: 'from local folder' },
    glossaryLabel: { id: 'languageStudio.documentTranslationDemoPage.glossaryLabel', defaultMessage: 'Glossary to use (Optional)' },
    azureFilesSource: { id: 'languageStudio.documentTranslationDemoPage.azureFilesSource', defaultMessage: 'from Azure blob storage' },
    downloadTranslatedFiles: {
        id: 'languageStudio.documentTranslationDemoPage.downloadTranslatedFiles',
        defaultMessage: 'Download translated file(s).'
    },
    destinationFileLabel: {
        id: 'languageStudio.documentTranslationDemoPage.destinationFileLabel',
        defaultMessage: 'Select file destination'
    },
    uploadDoc: {
        id: 'languageStudio.documentTranslationDemoPage.uploadDoc',
        defaultMessage: 'Upload your own document, browse a file in Azure storage, or use one of our sample documents'
    },
    selectDocsSectionTitle: {
        id: 'languageStudio.documentTranslationDemoPage.selectDocsSectionTitle',
        defaultMessage: 'Select some documents to try out'
    },
    documentUploaderDescription: {
        id: 'languageStudio.documentTranslationDemoPage.documentUploaderDescription',
        defaultMessage: `Document Translation supports current and legacy Office formats for Word, PowerPoint, Excel,
        and Outlook (.doc, .docx, .ppt, .pptx, .xls, .xlsx, and .msg); Plain and Rich Text (.txt and .rtf);
        HTML files (.html and .htm); Tab and Comma Separated Values (.tsv, .tab, .csv), XML Localization Interchange
        Files (.xlf, .xliff); and Open Document Text, Presentations, and Spreadsheets (.odt, .odp, and .ods);
        Adobe PDF (.pdf).`
    }
});

const theme = getScopedTheme('DocumentTranslationDemoPageInputStep');
const browseButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ label: { lineHeight: '16px' } });
const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { textAlign: 'center', fontSize: 14 } });
const separatorStyles = classNamesFunction<ISeparatorStyleProps, ISeparatorStyles>()({ root: { padding: 0, height: '1px' } });
const filesDescriptionTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { maxWidth: '200px' } });
const listContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { position: 'relative', flex: 1 } });
const scrollPaneStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    contentContainer: { overflowX: 'hidden' }
});
const listItemTextStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: { padding: '10px 20px', overflow: 'hidden' }
});
const listItemContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { '&:hover': { backgroundColor: theme.listItemHoverBackgroundColor } }
});

export const DocumentTranslationDemoPageInputStep = observer((props: DocumentTranslationDemoPageInputStepProps) => {
    const {
        selectedFiles,
        onFilesChange,
        storageAccounts,
        sourceLanguageKey,
        targetLanguageKeys,
        supportedLanguages,
        onGlossariesChange,
        onDestinationChange,
        storageAccountsError,
        onStorageAccountChange,
        selectedStorageAccount,
        onTargetLanguageChange,
        onSourceLanguageChange,
        refreshStorageAccounts,
        downloadTranslatedFiles,
        isStorageAccountsLoading,
        selectedDestinationContainer
    } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { uploadLocalFiles } = useTranslatorCapabilities();

    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isAzureContainerBrowsingHidden, setIsAzureContainerBrowsingHidden] = React.useState(true);
    const [isGlossariesFileBrowsingHidden, setIsGlossariesFileBrowsingHidden] = React.useState(true);
    const [azureContainerSelectionChecked, setAzureContainerSelectionChecked] = React.useState(false);
    const [isDestinationFileBrowsingHidden, setIsDestinationFileBrowsingHidden] = React.useState(true);
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });

    const { csAccountStore } = rootStore.azureStore.session;
    const storageAccount = storageAccounts.find(s => s.name === selectedStorageAccount);
    const fileSourceOptions: IContextualMenuItem[] = [
        { key: 'localFiles', text: localize(messages.localFilesSource), onClick: inputRef.current?.click },
        { key: 'azureFiles', text: localize(messages.azureFilesSource), onClick: () => setIsGlossariesFileBrowsingHidden(false) }
    ];

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    const submitSelectedFiles = (files: (AzureBlobStore | File)[], selectedContainer: string, shouldTranslateContainer: boolean) => {
        if (!isAzureContainerBrowsingHidden) {
            onFilesChange(
                files.map(f => ({ ...f, containerName: selectedContainer })),
                shouldTranslateContainer
            );
            setIsAzureContainerBrowsingHidden(true);
        } else if (!isDestinationFileBrowsingHidden) {
            onDestinationChange(selectedContainer, downloadTranslatedFiles);
            setIsDestinationFileBrowsingHidden(true);
        } else if (!isGlossariesFileBrowsingHidden) {
            onGlossariesChange(files.map(f => ({ ...f, containerName: selectedContainer })));
            setIsGlossariesFileBrowsingHidden(true);
        }
    };

    const handleLocalFilesSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files: filesList } = event.target as HTMLInputElement;
        const files = [...Array.from(filesList)];

        await uploadLocalFiles({
            files,
            storageAccountName: selectedStorageAccount,
            resourceGroupName: storageAccount?.resourceGroupName,
            containerName: `language-studio-glossaries-${getRandomNumber()}`,
            onComplete: containerName => onGlossariesChange(files.map(f => ({ ...f, containerName })))
        });
    };

    const renderFilesListRow = (item: TargetFile) => (
        <Stack horizontal data-is-focusable verticalAlign="center" horizontalAlign="space-between" styles={listItemContainerStyles}>
            <ExternalLink styles={listItemTextStyles} href={getBlobStoreUrl(storageAccount.id, item.containerName, item.name)}>
                {item.name}
            </ExternalLink>
            <IconButton
                iconProps={{ iconName: 'Delete' }}
                onClick={() =>
                    onFilesChange(
                        selectedFiles.filter(f => f.name !== item.name),
                        false
                    )
                }
            />
        </Stack>
    );

    const renderFilesList = () => (
        <Stack styles={listContainerStyles}>
            <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={scrollPaneStyles}>
                <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                    <List items={selectedFiles} onRenderCell={renderFilesListRow} />
                </FocusZone>
            </ScrollablePane>
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {!isValidResource.isValid && (
                <MessageBar messageBarType={MessageBarType.blocked} styles={{ innerText: { width: '100%' } }}>
                    <Stack>
                        {isValidResource.mainErrorMessage}
                        <DefaultButton
                            styles={{ root: { alignSelf: 'flex-end' } }}
                            onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                            {localize(messages.goToSettings)}
                        </DefaultButton>
                    </Stack>
                </MessageBar>
            )}

            <Stack tokens={{ childrenGap: 20, maxWidth: '70%' }}>
                <LuisText fontWeight={600} variant="xLarge">
                    {localize(messages.selectDocsSectionTitle)}
                </LuisText>

                <TranslatorDemosConfigs
                    needStorageAccount
                    isMultiTargetLanguageAvailable
                    storageAccounts={storageAccounts}
                    sourceLanguageKey={sourceLanguageKey}
                    targetLanguageKeys={targetLanguageKeys}
                    supportedLanguages={supportedLanguages}
                    storageAccountsError={storageAccountsError}
                    refreshStorageAccounts={refreshStorageAccounts}
                    selectedStorageAccount={selectedStorageAccount}
                    onSourceLanguageChange={onSourceLanguageChange}
                    onTargetLanguageChange={onTargetLanguageChange}
                    onStorageAccountChange={onStorageAccountChange}
                    isStorageAccountsLoading={isStorageAccountsLoading}
                />

                <Label required>{localize(messages.uploadDoc)}</Label>

                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <DragAndDropInput
                        extensionsToAccept={null}
                        disabled={!isValidResource.isValid || !selectedStorageAccount}
                        onDropFiles={files =>
                            uploadLocalFiles({
                                files,
                                storageAccountName: selectedStorageAccount,
                                resourceGroupName: storageAccount?.resourceGroupName,
                                containerName: `language-studio-${getRandomNumber()}`,
                                onComplete: containerName =>
                                    onFilesChange(
                                        files.map(f => ({ ...f, containerName, name: f.name })),
                                        true
                                    )
                            })
                        }
                        onRenderContent={d => (
                            <Stack tokens={{ childrenGap: 28, padding: 20 }}>
                                <Stack horizontalAlign="center">
                                    {d}
                                    <LuisText>{localize(messages.or)}</LuisText>
                                    <Link
                                        disabled={!isValidResource.isValid}
                                        onClick={() => setIsAzureContainerBrowsingHidden(false)}
                                        styles={linkStyles}>
                                        {localize(messages.browseAzure)}
                                    </Link>
                                </Stack>
                                <LuisText styles={filesDescriptionTextStyles}>{localize(messages.documentUploaderDescription)}</LuisText>
                            </Stack>
                        )}
                    />

                    <Stack grow styles={{ root: { border: `1px solid ${theme.borderColor}` } }}>
                        <Stack horizontal verticalAlign="center" tokens={{ padding: 10, childrenGap: 20 }}>
                            <Icon iconName="Page" />
                            <LuisText>{localize(messages.nameHeaderText)}</LuisText>
                        </Stack>
                        <Separator styles={separatorStyles} />
                        {renderFilesList()}
                    </Stack>
                </Stack>

                <Stack tokens={{ childrenGap: 5 }}>
                    <Label>{localize(messages.glossaryLabel)}</Label>
                    <ActionButton
                        styles={browseButtonStyles}
                        iconProps={{ iconName: 'Add' }}
                        disabled={!isValidResource.isValid}
                        text={localize(messages.addGlossary)}
                        menuProps={{ items: fileSourceOptions }}
                    />
                </Stack>

                <Stack tokens={{ childrenGap: 10 }}>
                    <Label>{localize(messages.destinationFileLabel)}</Label>

                    <Stack tokens={{ childrenGap: 5, padding: '0 0 0 40px' }}>
                        <Checkbox
                            disabled={!isValidResource.isValid}
                            label={localize(messages.browseAzure)}
                            checked={azureContainerSelectionChecked}
                            styles={{ label: { alignItems: 'flex-start' } }}
                            onChange={(_, checked) => setAzureContainerSelectionChecked(checked)}
                            onRenderLabel={(labelProps, defaultRenderer) => (
                                <Stack tokens={{ childrenGap: 5 }}>
                                    {defaultRenderer(labelProps)}
                                    {azureContainerSelectionChecked && (
                                        <Stack
                                            horizontal
                                            verticalAlign="center"
                                            tokens={{ childrenGap: 5 }}
                                            onClick={e => e.stopPropagation()}>
                                            {selectedDestinationContainer && [
                                                <ExternalLink
                                                    href={`https://ms.portal.azure.com/#blade/Microsoft_Azure_Storage/ContainerMenuBlade/overview/storageAccountId/${storageAccount.id
                                                        .split('/')
                                                        .join('%2F')}/path/${selectedDestinationContainer}`}>
                                                    {selectedDestinationContainer}
                                                </ExternalLink>,
                                                <Separator vertical />
                                            ]}
                                            <Link onClick={() => setIsDestinationFileBrowsingHidden(false)}>
                                                {localize(messages.browse)}
                                            </Link>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        />

                        <Checkbox
                            checked={downloadTranslatedFiles}
                            disabled={!isValidResource.isValid}
                            label={localize(messages.downloadTranslatedFiles)}
                            onChange={(_, checked) => onDestinationChange(selectedDestinationContainer, checked)}
                        />
                    </Stack>
                </Stack>

                <input multiple type="file" ref={inputRef} style={{ display: 'none' }} onChange={handleLocalFilesSelection} />

                <AzureContainerBrowsingModal
                    storageAccounts={storageAccounts}
                    selectedStorageAccount={selectedStorageAccount}
                    onStorageAccountChange={onStorageAccountChange}
                    isStorageAccountsLoading={isStorageAccountsLoading}
                    useRemoteContainer={!isDestinationFileBrowsingHidden}
                    hidden={isAzureContainerBrowsingHidden && isDestinationFileBrowsingHidden && isGlossariesFileBrowsingHidden}
                    onSubmit={({ files, selectedContainer, isAllFiles }) => submitSelectedFiles(files, selectedContainer, isAllFiles)}
                    onDismiss={() => {
                        setIsAzureContainerBrowsingHidden(true);
                        setIsGlossariesFileBrowsingHidden(true);
                        setIsDestinationFileBrowsingHidden(true);
                    }}
                />
            </Stack>
        </Stack>
    );
});
